import type { ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { safeNext, signInPath } from '@/lib/nav';
import { useOperator } from '@/lib/operator';
import type { PlatformPermission } from '@/lib/platform';

import { Booting, Forbidden, Unreachable } from './states';

/**
 * Everything behind sign-in. Signed out → `/sign-in?next=<here>`; the sign-in
 * page sends them back with `safeNext`.
 */
export function RequireOperator({ children }: { children: ReactNode }) {
  const { status, error, reload } = useOperator();
  const location = useLocation();

  if (status === 'checking') return <Booting />;
  if (status === 'unreachable') return <Unreachable error={error} onRetry={() => void reload()} />;
  if (status === 'signed-out') return <Navigate to={signInPath(location)} replace />;

  return children;
}

/** The sign-in page. Already signed in → wherever `next` says, or home. */
export function PublicOnly({ children }: { children: ReactNode }) {
  const { status } = useOperator();
  const [params] = useSearchParams();

  if (status === 'checking') return <Booting />;
  if (status === 'signed-in') return <Navigate to={safeNext(params.get('next'))} replace />;

  return children;
}

/**
 * A route the operator's rank does not reach shows why, rather than
 * redirecting — a redirect hides the reason and a bookmark that silently
 * lands somewhere else reads as a bug. The API refuses regardless; this is
 * courtesy, not control.
 */
export function RequirePermission({ permission, children }: { permission: PlatformPermission; children: ReactNode }) {
  const { has } = useOperator();

  return has(permission) ? children : <Forbidden permission={permission} />;
}
